import type {FC, MouseEvent} from 'react';
import {useSelector} from 'react-redux';
import {TemplateDesign, TemplateDetails} from '~/GalleryPB/components/Template/Template';
import {
  selectGalleryDataCurrentPage,
  selectGalleryDataTracking,
} from '~/GalleryPB/reducers/galleryData';
import type {GalleryPromoTileConfig} from '~/GalleryPB/types/gallery';
import type {Template, VariantType} from '~/GalleryPB/types/template';
import {buildHref, pickSrc} from '~/GalleryPB/utils';
import {GalleryPromoTile} from './GalleryPromoTile/GalleryPromoTile';

interface TemplateRowProps {
  results: (Template | GalleryPromoTileConfig)[];
  screen: 'mw' | 'desktop';
  type: VariantType;
  onTemplateClick: (e: MouseEvent, templateId: string, href: string) => void;
  categoryId?: string;
  favorites: string[];
  loading?: 'eager' | 'lazy';
  onClickQuickView: (templateId: string) => void;
  rowCount: number;
}

export const TemplateRow: FC<TemplateRowProps> = ({
  results,
  screen,
  type,
  onTemplateClick,
  categoryId,
  favorites,
  loading = 'eager',
  onClickQuickView,
  rowCount,
}) => {
  const tracking = useSelector(selectGalleryDataTracking);
  const currentPage = useSelector(selectGalleryDataCurrentPage);

  const fabric = 'fabric ';

  const isCard = !!window.cards_page;

  const desktop = screen === 'desktop';
  if (rowCount < results.length) evite.error('Too many results in TemplateRow');

  return (
    <div className="template-row">
      <div
        className={`${fabric}gallery-template__row-${type}${isCard ? '__card' : '__invitation'}`}
        data-qa-id="gallery-templates__row"
      >
        {results.map((result, i) => {
          if (!('templateId' in result)) {
            return (
              <GalleryPromoTile
                key={`promo-tile-${result.name?.replace(/\s+/g, '')}`}
                data={result}
                dataQaId="af-success-disc-amazon"
              />
            );
          }

          const src = pickSrc(result, desktop, true);
          const href = buildHref(result, tracking, currentPage);

          if (!result.assets || !src) {
            const missingString = [!result.assets && 'assets', !src && 'src']
              .filter(Boolean)
              .join(' and ');
            evite.error(
              `Template ${result.templateId} is missing ${missingString}, removing it from the row`
            );
            return null;
          }

          return (
            src && (
              <TemplateDesign
                key={result.templateId}
                result={{
                  ...result,
                  src,
                  categoryId,
                  position: i + 1,
                }}
                screen={screen}
                href={href}
                onTemplateClick={onTemplateClick}
                loading={loading}
                onClickQuickView={onClickQuickView}
                favorited={favorites.includes(result.templateId)}
              />
            )
          );
        })}
      </div>
      <div
        className={`${fabric}gallery-template__row-${type}${isCard ? '__card' : '__invitation'}`}
      >
        {results.map((result, i) => {
          if (!('templateId' in result)) return null; // It's a gallery promo tile, not a template
          const src = pickSrc(result, desktop, true);
          const href = buildHref(result, tracking, currentPage);
          return (
            src && (
              <TemplateDetails
                key={`td-${result.templateId}`}
                result={{
                  ...result,
                  categoryId,
                  position: i + 1,
                }}
                href={href}
                onTemplateClick={onTemplateClick}
              />
            )
          );
        })}
      </div>
    </div>
  );
};
